import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { CustomPagination } from 'components/featured/Deals/components/DealsTable/Pagination';
import { SortPopover } from 'components/shared/SortPopOver';

import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';

import { ReactComponent as VerticalDotsIcon } from 'assets/vertical-dots.svg';
import SectionLoader from 'components/shared/SectionLoader';
import { formatDealAmount } from 'helper/short-currency';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  BusinessType,
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
} from 'store/slices/businessesSlice';
import Logo from '../Logo';
import NoResults from '../NoResults';
import style from './style.module.css';
import styles from './styles';
import { usePermissions } from 'customHook/usePermissions';

export interface Column {
  id:
    | 'name'
    | 'category'
    | 'industry'
    | 'activeDealCount'
    | 'totalDealCount'
    | 'wonValue'
    | 'successRate'
    | 'avgVelocity'
    | 'ourContacts'
    | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

export const Columns: readonly Column[] = [
  { id: 'name', label: 'Name' },
  { id: 'category', label: 'Category' },
  { id: 'industry', label: 'Industry' },
  { id: 'activeDealCount', label: 'Active Deals' },
  { id: 'totalDealCount', label: 'Total Deals' },
  { id: 'wonValue', label: 'Won Value' },
  { id: 'successRate', label: 'Success Rate' },
  { id: 'avgVelocity', label: 'Average Velocity' },
  { id: 'ourContacts', label: 'Our Contacts' },
  { id: 'actions', label: '', align: 'right' },
];

const SORTABLE_COLUMNS = [
  'name',
  'activeDealCount',
  'totalDealCount',
  'wonValue',
  'successRate',
  'avgVelocity',
];

const SORT_LABELS: Record<string, { asc: string; desc: string }> = {
  activeDealCount: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  totalDealCount: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  avgVelocity: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  wonValue: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  successRate: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
};

const TableView = ({
  handleOpenSettings,
  isMybusinessesTab,
}: {
  handleOpenSettings: (event: MouseEvent<SVGSVGElement>, data: BusinessType) => void;
  isMybusinessesTab: boolean;
}) => {
  const { hasAction, hasFullAccess } = usePermissions();
  const canViewDetails =
    hasAction('businesses', 'BusinessDetailsRelationship', 'view') || hasFullAccess('businesses');
  const [sortAnchor, setSortAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>();
  const dispatch = useAppDispatch();

  const {
    businesses,
    loading,
    pagination: { page, pageSize, total, totalPages },
  } = useAppSelector((state) => state.businesses);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      dispatch(setPage(newPage + 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsChange = (newRows: number) => {
    dispatch(setPageSize(newRows));
    dispatch(setPage(1));
  };

  if (loading === 'in-progress') return <SectionLoader />;
  if (!businesses.length) return <NoResults isMybusinessesTab={isMybusinessesTab} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'auto',
      }}
    >
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            height: 'calc(100vh - 290px)',
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow sx={headerRowStyles}>
                {Columns.map((column) => {
                  const isSortable = SORTABLE_COLUMNS.includes(column.id);

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      onClick={(e) =>
                        isSortable && setSortAnchor({ el: e.currentTarget, id: column.id })
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body3'>{column.label}</Typography>
                        {isSortable && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label='button'
                          >
                            <UnfoldMoreIcon
                              sx={{
                                fontSize: 16,
                                color: zeplinColor.BasicBlack,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {businesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={Columns.length} align='center'>
                    <Typography variant='body2'>No businesses found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                businesses.map((row, ind) => {
                  const { businessId } = row;

                  return (
                    <TableRow
                      hover
                      key={ind}
                      component={canViewDetails ? Link : Box}
                      to={canViewDetails ? `${RoutePaths.Businesses}/${businessId}` : '#'}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          borderBottom: `2px solid ${zeplinColor.Neutral5}`,
                        },
                        textDecoration: 'none',
                        color: 'inherit',
                        bgcolor: 'none',
                        borderLeft: `2px solid ${zeplinColor.Neutral5}`,
                        cursor: 'pointer',
                      }}
                    >
                      {Columns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            onClick={(e) => {
                              if (column.id === 'actions') {
                                e.stopPropagation();
                              }
                            }}
                            sx={{
                              padding: '22px 12px',
                              borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                              borderTop: `1px solid ${zeplinColor.Neutral5}`,
                            }}
                          >
                            {renderCell(column.id, row, handleOpenSettings)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchor?.el || null}
        setAnchorEl={() => setSortAnchor(null)}
        sortOption={sortConfig && sortConfig.field === sortAnchor?.id ? sortConfig.order : 'asc'}
        onSortChange={(order) => {
          if (sortAnchor) {
            setSortConfig({ field: sortAnchor.id, order });
            dispatch(setSortOrder(order));
            dispatch(setSortBy(sortAnchor.id));
            dispatch(setPage(0));
          }
          setSortAnchor(null);
        }}
        label={Columns.find((c) => c.id === sortAnchor?.id)?.label || ''}
        ascLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.asc : ''}
        descLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.desc : ''}
      />

      <CustomPagination
        currentPage={page - 1}
        totalPages={totalPages}
        rowsPerPage={pageSize}
        totalCount={total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
      />
    </Box>
  );
};

const headerRowStyles = {
  '& th': { backgroundColor: '#f5f5f5', borderBottom: 'none' },
  '& th:first-of-type': { borderRadius: '14px 0 0 14px' },
  '& th:last-of-type': { borderRadius: '0 14px 14px 0' },
};

const renderCell = (
  columnId: string,
  row: any,
  handleOpenSettings: (event: MouseEvent<SVGSVGElement>, data: BusinessType) => void,
) => {
  const {
    name,
    logoUrl,
    category,
    activeDealCount,
    totalDealCount,
    wonValue,
    successRate,
    avgVelocity,
    ourContacts,
    industry,
  } = row;

  const renders: Record<string, () => JSX.Element | string> = {
    name: () => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '240px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '100%',
            flexShrink: 0,
          }}
        />
        <Box sx={{ width: '40px', height: '40px' }}>
          <Logo logo={logoUrl} />
        </Box>
        <Typography noWrap sx={{ textOverflow: 'ellipsis' }}>
          {name}
        </Typography>
      </Box>
    ),
    category: () => (
      <Chip
        label={<Typography variant='label1'>{category ?? 'N/A'}</Typography>}
        sx={{
          bgcolor: zeplinColor.BasicBlack,
          color: 'white',
          height: '22px',
          fontSize: '12px',
        }}
      />
    ),
    industry: () => {
      return (
        <Chip
          label={
            <Typography variant='label1' sx={{ color: zeplinColor.BasicBlack }}>
              {industry ?? 'N/A'}
            </Typography>
          }
          sx={{
            height: '22px',
          }}
        />
      );
    },
    activeDealCount: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{activeDealCount ?? 0}</Typography>
      </Box>
    ),
    totalDealCount: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{totalDealCount ?? 0}</Typography>
      </Box>
    ),
    wonValue: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{formatDealAmount(wonValue) ?? 'N/A'}</Typography>
      </Box>
    ),
    successRate: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{successRate ?? 'N/A'}</Typography>
      </Box>
    ),
    avgVelocity: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{`${avgVelocity ? `${avgVelocity}d` : '-'}`}</Typography>
      </Box>
    ),
    ourContacts: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{ourContacts ?? '-'}</Typography>
      </Box>
    ),
    actions: () => (
      <Box sx={styles.optionMenu}>
        <VerticalDotsIcon
          className={style['vertical-icon']}
          onClick={(e: MouseEvent<SVGSVGElement>) => {
            e.preventDefault();
            handleOpenSettings(e, row);
          }}
        />
      </Box>
    ),
  };

  return renders[columnId] ? renders[columnId]() : (row as any)[columnId];
};

export default TableView;
